import React, { useState } from 'react';
import styled from 'styled-components';
import { Fade } from 'react-awesome-reveal';
import Swal from 'sweetalert2';
import KidsCodePaystackCardPayment from './KidsCodePaystackCardPayment';
import ClassRegistrationNotice from './ClassRegistrationNotice';
import AdultClassRegistrationAndPayment from './AdultClassRegistrationAndPayment';

const PageContainer = styled.div`
  background-color: #f0f4f8;
  color: #1a1a1a;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow-x: hidden;
  padding: 0;
  margin: 0;
  width: 100%;
  margin-top: 10px;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const Section = styled.section`
  padding: 10px;
  margin: 10px auto;
  max-width: 1200px;
`;

const HeroSection = styled(Section)`
  text-align: center;
  padding: 10px;
  background: linear-gradient(135deg, #e0f2fe, #bae6fd);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
  box-shadow: 0 2px 10px rgba(2, 132, 199, 0.05);
`;

const Badge = styled.span`
  background: #e0f2fe;
  color: #0284c7;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border: 1px solid #7dd3fc;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #0f172a;
  margin: 10px 0;
  line-height: 1.2;
  font-weight: 900;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #334155;
  margin: 0 auto;
  max-width: 700px;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const SectionHeader = styled.h2`
  font-size: 22px;
  background: linear-gradient(135deg, #0284c7, #0369a1, #075985);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  margin: 10px 0;
  font-weight: 800;
  letter-spacing: -0.5px;
  word-break: break-word;
`;

const MotivationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 10px;
  margin: 10px 0;
`;

const MotivationCard = styled.div`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 10px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
  border-left: 4px solid #0284c7;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  color: #1e293b;
  margin: 0;
  font-weight: 700;
  word-break: break-word;
`;

const CardText = styled.p`
  font-size: 13px;
  color: #475569;
  margin: 0;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const FormWrapper = styled.div`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  margin: 10px 0;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const FormNotice = styled.div`
  background: #e0f2fe;
  border-left: 4px solid #0284c7;
  padding: 10px;
  font-size: 13px;
  color: #0369a1;
  border-radius: 0 6px 6px 0;
  margin-bottom: 10px;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const FeedbackMessage = styled.div`
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  background: ${({ isError }) => (isError ? '#fee2e2' : '#d1fae5')};
  color: ${({ isError }) => (isError ? '#991b1b' : '#065f46')};
  border: 1px solid ${({ isError }) => (isError ? '#f87171' : '#34d399')};
  word-break: break-word;
  overflow-wrap: break-word;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: ${({ fullWidth }) => (fullWidth ? 'span 2' : 'span 1')};
  word-break: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  word-break: break-word;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;
  word-break: break-word;

  &:focus {
    border-color: #0284c7;
    background: #ffffff;
  }
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #0284c7;
    background: #ffffff;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 6px;
  grid-column: span 2;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  padding: 10px;
  border-radius: 6px;
  word-break: break-word;

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const Checkbox = styled.input`
  margin-top: 2px;
  accent-color: #0284c7;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 13px;
  color: #0369a1;
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #0284c7, #0369a1);
  color: #ffffff;
  border: none;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.95;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function AdultApplicationPage() {
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', isError: false });

  const [formData, setFormData] = useState({
    fullName: '',
    course: '',
    address: '',
    dateOfBirth: '',
    phoneNumber: '',
    emailAddress: '',
    city: '',
    gender: '',
    previousProgram: '',
    referralSource: '',
    paymentAck: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const confirmResult = await Swal.fire({
      title: 'Submit Application?',
      text: 'Are you sure you want to submit this registration application?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0284c7',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Submit',
      cancelButtonText: 'Cancel'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setSubmitting(true);
    setFeedback({ message: '', isError: false });

    Swal.fire({
      title: 'Submitting Application...',
      text: 'Please wait while we process your request.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });

    try {
      const response = await fetch('https://elexdondigitalacademy.com/api/process_kids_code_application.php', {
        method: 'POST',
        body: data,
      });
      const result = await response.json();

      if (result.success) {
        const successMsg = result.message + ` (Ref: ${result.reference})`;
        setFeedback({ message: successMsg, isError: false });
        
        setFormData({
          fullName: '',
          course: '',
          address: '',
          dateOfBirth: '',
          phoneNumber: '',
          emailAddress: '',
          city: '',
          gender: '',
          previousProgram: '',
          referralSource: '',
          paymentAck: false,
        });

        Swal.fire({
          title: 'Success!',
          text: successMsg,
          icon: 'success',
          confirmButtonColor: '#0284c7'
        });
      } else {
        const errorMsg = result.error || 'An error occurred while submitting.';
        setFeedback({ message: errorMsg, isError: true });

        Swal.fire({
          title: 'Submission Failed',
          text: errorMsg,
          icon: 'error',
          confirmButtonColor: '#0284c7'
        });
      }
    } catch (err) {
      const networkErrorMsg = 'Network or server error. Please try again.';
      setFeedback({ message: networkErrorMsg, isError: true });

      Swal.fire({
        title: 'Connection Error',
        text: networkErrorMsg,
        icon: 'error',
        confirmButtonColor: '#0284c7'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      {/* Hero Section */}
      <Fade triggerOnce>
        <HeroSection>
          <Badge>🚀 Accelerate Your Tech Career</Badge>
          <Title>Master In-Demand Tech Skills Today</Title>
          <Subtitle>
            Equip yourself with professional-grade software development, artificial intelligence literacy, and hands-on technical proficiency designed for industry leaders.
          </Subtitle>
        </HeroSection>
      </Fade>

      {/* Why Enroll Section */}
      <Fade triggerOnce>
        <Section>
          <SectionHeader>Why Professionals Choose Us</SectionHeader>
          <MotivationGrid>
            <MotivationCard>
              <CardTitle>💡 Advanced Problem-Solving</CardTitle>
              <CardText>Train your mind to engineer scalable software solutions and architectural patterns used in modern enterprise environments.</CardText>
            </MotivationCard>
            <MotivationCard style={{ borderLeftColor: '#0369a1' }}>
              <CardTitle>🤖 Industry-Ready AI & Tools</CardTitle>
              <CardText>Learn cutting-edge frameworks, automated workflows, and modern development tools to stay competitive in the digital economy.</CardText>
            </MotivationCard>
            <MotivationCard style={{ borderLeftColor: '#075985' }}>
              <CardTitle>🏆 Practical Portfolio Projects</CardTitle>
              <CardText>Build and deploy production-ready projects that showcase your capabilities to prospective employers and clients.</CardText>
            </MotivationCard>
          </MotivationGrid>
        </Section>
      </Fade>

       <AdultClassRegistrationAndPayment/>

      {/* Application Form Section */}
      <Fade triggerOnce>
        <Section>
          <SectionHeader>Professional Application</SectionHeader>
          <FormWrapper>
            <FormNotice>
              Ensure that your details are accurate as your certification and professional records will be generated from them.
            </FormNotice>

            {feedback.message && (
              <FeedbackMessage isError={feedback.isError}>{feedback.message}</FeedbackMessage>
            )}

            <form onSubmit={handleSubmit}>
              <FormGrid>
                <FormGroup fullWidth>
                  <Label>Select Course *</Label>
                  <Select
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Course</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Mobile App building">Mobile App building</option>
                    <option value="Robotics and Hardware Engineering">Robotics and Hardware Engineering</option>
                    <option value="Artificial Intelligence">Artificial Intelligence</option>
                    <option value="Product Design UIUX">Product Design / UIUX</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Address *</Label>
                  <Input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter your address"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Date of Birth *</Label>
                  <Input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="Example: +1 212-695-1962"
                    required
                  />
                </FormGroup>

                <FormGroup fullWidth>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleChange}
                    placeholder="Example: user@website.com"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>City *</Label>
                  <Input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Gender *</Label>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Have you attended any of our programs before? *</Label>
                  <Select
                    name="previousProgram"
                    value={formData.previousProgram}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select option</option>
                    <option value="YES">YES</option>
                    <option value="NO">NO</option>
                  </Select>
                </FormGroup>

                <FormGroup fullWidth>
                  <Label>How Did You Find Out About Us? *</Label>
                  <Select
                    name="referralSource"
                    value={formData.referralSource}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select referral source</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Google">Google</option>
                    <option value="Whatsapp">Whatsapp</option>
                    <option value="Church">Church</option>
                    <option value="Hand Bills">Hand Bills</option>
                    <option value="Family and Friends">Family and Friends</option>
                  </Select>
                </FormGroup>

                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="payment-ack"
                    name="paymentAck"
                    checked={formData.paymentAck}
                    onChange={handleChange}
                    required
                  />
                  <CheckboxLabel htmlFor="payment-ack">
                    I understand that I will have to pay for the training *
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGrid>

              <SubmitButton type="submit" disabled={submitting}>
                {submitting ? 'Submitting Application...' : 'Submit Application'}
              </SubmitButton>
            </form>
          </FormWrapper>
        </Section>
      </Fade>
    </PageContainer>
  );
}